import { setCookieHasRefreshToken } from "../../../lib/cookies/cookieHasRefreshToken.cookie";
import { useRequestMutation } from "../../../request/hooks/useRequestMutation";
import { useSession } from "../../../session/hooks/useSession";
import { useRequestExistsEmail } from "../exists/useRequestExistsEmail";
import { useRequestMagicLink } from "../magic-link/useRequestMagicLink";
import type { Auth } from "./types/schema/authSchema.types";
import {
  authSchema,
  booleanSuccessSchema,
} from "./types/schema/authSchema.types";

export type UTMProps = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
};

type ConsentProps = {
  consent: number[];
};

type GoogleAuthProps = {
  token: string;
  auth_flow: string;
} & UTMProps &
  ConsentProps;

type AppleAuthProps = {
  token: string;
  name?: string;
  last_name?: string;
  redirect?: string;
} & UTMProps &
  ConsentProps;

type SimpleAuthProps = (
  | {
      email: string;
      password: string;
      captcha: string;
    }
  | {
      phone: string;
      otp: string;
    }
) &
  UTMProps &
  ConsentProps;

type OtpAuthProps = (
  | {
      email: string;
      otp: string;
    }
  | {
      phone: string;
      otp: string;
    }
) &
  UTMProps &
  ConsentProps;

export const useRequestAuth = () => {
  const { setStatus, setToken } = useSession();
  const onSuccess = (data: Auth) => {
    setCookieHasRefreshToken({ value: true });
    setToken(data.oauth.access_token);
    setStatus("authenticated");
    return new Promise<void>((r) => setTimeout(r, 10));
  };

  return {
    mutationEmailExists: useRequestExistsEmail({ onSuccess }).mutationPost,
    mutationLogin: useRequestMutation({
      mutationFnRequestProps: (data: { email: string; password: string }) => ({
        body: JSON.stringify(data),
        method: "POST",
        urlBackendPath: "/login",
        schema: authSchema,
        requestType: "default",
      }),
      onMutate: () => ({ noRefresh: true }),
      onSuccess,
    }),
    mutationMagicLink: useRequestMagicLink({ onSuccess }).mutationPost,
    mutationRegister: useRequestMutation({
      mutationFnRequestProps: (data: SimpleAuthProps) => ({
        body: JSON.stringify(data),
        method: "POST",
        urlBackendPath: "/register",
        schema: authSchema,
        requestType: "default",
      }),
      onMutate: () => ({ noRefresh: true }),
      onSuccess,
    }),
    mutationOtpVerify: useRequestMutation({
      mutationFnRequestProps: (data: OtpAuthProps) => ({
        body: JSON.stringify(data),
        method: "POST",
        urlBackendPath: "/otp/verify",
        schema: authSchema,
        requestType: "default",
      }),
      onMutate: () => ({ noRefresh: true }),
      onSuccess,
    }),
    mutationGoogleAuth: useRequestMutation({
      mutationFnRequestProps: (data: GoogleAuthProps) => ({
        body: JSON.stringify(data),
        method: "POST",
        requestType: "default",
        schema: authSchema,
        urlBackendPath: "/social/google",
      }),
      onMutate: () => ({ noRefresh: true }),
      onSuccess,
    }),
    mutationAppleAuth: useRequestMutation({
      mutationFnRequestProps: (data: AppleAuthProps) => ({
        body: JSON.stringify(data),
        method: "POST",
        urlBackendPath: "/social/apple",
        schema: authSchema,
        requestType: "default",
      }),
      onMutate: () => ({ noRefresh: true }),
      onSuccess,
    }),
    mutationForgotPassword: useRequestMutation({
      mutationFnRequestProps: (data: { email: string }) => ({
        body: JSON.stringify(data),
        method: "POST",
        urlBackendPath: "/password/forgot",
        schema: booleanSuccessSchema,
        requestType: "default",
      }),
      onMutate: () => ({ noRefresh: true }),
    }),
  };
};
